import axios from "axios";
import { getDistance } from "geolib";
import qualPApi from "../../../service/modules/qualP.api";
import { useSelector } from "react-redux";
import { z } from "zod";

/** Códigos de `activeStatus` (paradas) — enviar na query da API, não o rótulo PT */
export const STOPPING_POINT_ACTIVE_STATUS_CODES = [
  "LOADING_PENDING",
  "HEADING_TO_PICKUP",
  "WAITING_LOADING",
  "WAITING_DOCUMENTATION",
  "LOADING_COMPLETED",
  "UNLOADING_PENDING",
  "IN_TRANSIT",
  "WAITING_UNLOADING",
  "UNLOADING_COMPLETED",
];

const simulationSchema = z.object({
  stopping_points: z.array(
    z.object({
      // logradouro: z.string().trim().min(1, "Informe o endereço."),
      city: z.string().trim().min(1, 'Por favor, informe a cidade.'),
      // number: z.coerce.string().optional().default(""),
      uf: z.string().trim().min(2, "Por favor, informe o estado."),
    }),
  ).min(2, 'Os campos "origem" e "destino" são obrigatórios.'),

  cargoType: z.string().trim().min(1, 'Por favor, informe o tipo de carga (ex: granel, container, carga geral).'),
  valueNF: z.coerce.number().min(1, "Informe o valor da carga (em R$)."),
  weightCargo: z.coerce.number().min(1, "Por favor, informe o peso da carga."),
  axles: z.coerce.number().min(1, "Quantidade de eixos inválida. Informe um valor entre 2 e 9."),
});

export function translatorForTheStatusOfStoppingPoints(status) {
  switch (status) {
    case "LOADING_PENDING":
      return "Criado, não iniciado";
    case "HEADING_TO_PICKUP":
      return "Deslocando-se para a coleta";
    case "WAITING_LOADING":
      return "Aguardando Carregamento";
    case "WAITING_DOCUMENTATION":
      return "Aguardando Documentação";
    case "LOADING_COMPLETED":
      return "Etapa de carregamento finalizada";
    case "UNLOADING_PENDING":
      return "Criado, não iniciado";
    case "IN_TRANSIT":
      return "Em trânsito";
    case "WAITING_UNLOADING":
      return "Aguardando Descarregamento";
    case "UNLOADING_COMPLETED":
      return "Etapa de descarregamento finalizada";
    default:
      return [];
  }
} // stoppingPoints

export const STATUS_DESCRIBE_BY_CODE = {
  "00": "Pendente de validação",
  "01": "Pendente de contratação",
  "02": "Em Analise de perfil",
  "03": "Em Analise do motorista",
  "04": "Contratado",
  "05": "Em transito",
  "06": "Entregue",
  "07": "Finalizado",
  "08": "Cancelado",
};

export const FREIGHT_STATUS_PENDING_VALIDATION = {
  code: "00",
  describe: "Pendente de validação",
};

export const FREIGHT_STATUS_PENDING_CONTRACT = {
  code: "01",
  describe: "Pendente de contratação",
};

export const FREIGHT_STATUS_CONTRACTED = {
  code: "04",
  describe: "Contratado",
};

/** Códigos em que o frete ainda pode ser cancelado (00–04) */
export const FREIGHT_STATUS_CANCELABLE_CODES = ["00", "01", "02", "03", "04"];

/**
 * Normaliza `financial.validateAllSimulations` (Firestore pode gravar string).
 */
export function normalizeValidateAllSimulationsFlag(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null || value === "") {
    return false;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === "true" ||
      normalized === "1" ||
      normalized === "yes" ||
      normalized === "sim"
    );
  }
  return false;
}

/**
 * Status pendente inicial (sem motorista).
 * Validação (00): embarcador do usuário logado com `financial.validateAllSimulations`.
 * Contratação (01): demais casos.
 * @param {boolean} validateAllSimulations - `financial.validateAllSimulations` do embarcador logado
 */
export function getFreightPendingStatusByValidateAllSimulations(
  validateAllSimulations = false,
) {
  return normalizeValidateAllSimulationsFlag(validateAllSimulations)
    ? { ...FREIGHT_STATUS_PENDING_VALIDATION }
    : { ...FREIGHT_STATUS_PENDING_CONTRACT };
}

/**
 * Define `status` ao criar/atualizar frete.
 * Em P. Validação (00), o status só avança via Liberar Frete (`releaseFreight`),
 * exceto quando já há motorista ou transportadora atribuída.
 * Com motorista ou transportadora → Contratado (04); senão → 00 ou 01 conforme a flag.
 */
export function resolveFreightStatusForSave({
  uidDriver,
  validateAllSimulations = false,
  currentStatusCode = null,
  releaseFreight = false,
  hasTransport = false,
  idTransport = null,
} = {}) {
  const hasDriver = String(uidDriver ?? "").trim() !== "";
  const transportAssigned =
    hasTransport === true ||
    String(idTransport ?? "").trim() !== "";
  const normalizedCurrentCode =
    currentStatusCode != null && String(currentStatusCode).trim() !== ""
      ? String(currentStatusCode).padStart(2, "0")
      : null;

  if (releaseFreight) {
    if (hasDriver || transportAssigned) {
      return { ...FREIGHT_STATUS_CONTRACTED };
    }
    return getFreightPendingStatusByValidateAllSimulations(false);
  }

  const isCurrentlyPendingValidation =
    normalizedCurrentCode === FREIGHT_STATUS_PENDING_VALIDATION.code;
  const newFreightWithValidation =
    !normalizedCurrentCode &&
    normalizeValidateAllSimulationsFlag(validateAllSimulations);

  // P. Validação só se ainda não houver motorista nem transportadora (igual aceite em lances).
  if (
    (isCurrentlyPendingValidation || newFreightWithValidation) &&
    !hasDriver &&
    !transportAssigned
  ) {
    return { ...FREIGHT_STATUS_PENDING_VALIDATION };
  }

  if (hasDriver || transportAssigned) {
    return { ...FREIGHT_STATUS_CONTRACTED };
  }

  return getFreightPendingStatusByValidateAllSimulations(
    validateAllSimulations,
  );
}

/** Textos de `status.describe` do frete (00–08) — usados no filtro e na query `status` */
export const FREIGHT_STATUS_DESCRIBE_OPTIONS = Object.values(
  STATUS_DESCRIBE_BY_CODE,
);

const STATUS_CODE_BY_DESCRIBE = {
  "Pendente de validação": "00",
  "P. Validação": "00",
  "Pendente de contratação": "01",
  "Em Analise de perfil": "02",
  "Em Analise do motorista": "03",
  Contratado: "04",
  "Em transito": "05",
  "Em trânsito": "05",
  Entregue: "06",
  Finalizado: "07",
  Cancelado: "08",
};

/**
 * Converte describe do filtro de status para código (00–08) na query GET /freight/v2.
 * Evita perder registros com variações de texto (ex.: "P. Validação" vs "Pendente de validação").
 */
export function mapFreightStatusFilterForV2Api(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const normalized = normalizeFreightStatusDescribe(raw);
  if (FREIGHT_STATUS_DESCRIBE_OPTIONS.includes(normalized)) {
    return resolveStatusCode({ describe: normalized }, normalized);
  }

  if (STATUS_CODE_BY_DESCRIBE[raw]) {
    return STATUS_CODE_BY_DESCRIBE[raw];
  }

  return raw;
}

/**
 * Unifica variações vindas do banco/salvamento (C, CANCELADO, cancelado) para o rótulo do filtro/UI.
 */
export function normalizeFreightStatusDescribe(raw) {
  const s = String(raw ?? "")
    .trim()
    .replace(/\s+/g, " ");
  if (!s) return "";
  const folded = s
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toUpperCase();
  if (folded === "C") return "Cancelado";
  if (folded === "CANCELADO" || folded === "CANCELADA") return "Cancelado";
  return s;
}

export const resolveStatusDescribe = (statusValue, fallbackCode = "") => {
  if (typeof statusValue === "string" && statusValue.trim()) {
    return normalizeFreightStatusDescribe(statusValue.trim());
  }

  const fromObject =
    statusValue?.describe ||
    statusValue?.described ||
    statusValue?.description ||
    statusValue?.status_text ||
    "";

  if (typeof fromObject === "string" && fromObject.trim()) {
    return normalizeFreightStatusDescribe(fromObject.trim());
  }

  const codeValue = String(statusValue?.code || fallbackCode || "");
  const fromCode = STATUS_DESCRIBE_BY_CODE[codeValue] || "";
  return normalizeFreightStatusDescribe(fromCode);
};

export const resolveStatusCode = (statusValue, fallbackDescribe = "") => {
  const rawCode = statusValue?.code;
  if (rawCode != null && String(rawCode).trim() !== "") {
    return String(rawCode);
  }

  const describeValue = resolveStatusDescribe(statusValue).trim();
  if (STATUS_CODE_BY_DESCRIBE[describeValue]) {
    return STATUS_CODE_BY_DESCRIBE[describeValue];
  }

  const fallbackNorm = normalizeFreightStatusDescribe(fallbackDescribe).trim();
  if (fallbackNorm && STATUS_CODE_BY_DESCRIBE[fallbackNorm]) {
    return STATUS_CODE_BY_DESCRIBE[fallbackNorm];
  }

  if (STATUS_CODE_BY_DESCRIBE[fallbackDescribe]) {
    return STATUS_CODE_BY_DESCRIBE[fallbackDescribe];
  }

  return "01";
};

export function isFreightPendingValidationStatus(
  statusValue,
  fallbackDescribe = "",
) {
  return (
    resolveStatusCode(statusValue, fallbackDescribe) ===
    FREIGHT_STATUS_PENDING_VALIDATION.code
  );
}

/**
 * Em P. Validação (00), salvar só atualiza dados — status só muda via Liberar Frete.
 */
export function preservePendingValidationStatusIfNeeded({
  chosenStatus,
  currentStatusValue,
  fallbackDescribe = "",
  releaseFreight = false,
  isFinalize = false,
} = {}) {
  if (releaseFreight || isFinalize) return chosenStatus;

  if (isFreightPendingValidationStatus(currentStatusValue, fallbackDescribe)) {
    return { ...FREIGHT_STATUS_PENDING_VALIDATION };
  }

  return chosenStatus;
}

/**
 * Usuário com permissão "Não pode liberar frete": mantém o frete em P. Validação (00)
 * enquanto estiver nos status iniciais (00–04). Não altera fretes já em trânsito/finalizados.
 */
export function applyCannotReleaseFreightStatus(
  chosenStatus,
  cannotReleaseFreight = false,
) {
  if (cannotReleaseFreight) return chosenStatus;

  const code = resolveStatusCode(chosenStatus, chosenStatus?.describe);
  if (FREIGHT_STATUS_CANCELABLE_CODES.includes(code)) {
    return { ...FREIGHT_STATUS_PENDING_VALIDATION };
  }

  return chosenStatus;
}

export const hasCargaOperation = (operation) => {
  if (Array.isArray(operation)) {
    return operation.includes("carga");
  }

  return operation === "carga";
};

export const hasDescargaOperation = (operation) => {
  if (Array.isArray(operation)) {
    return operation.includes("descarga");
  }

  return operation === "descarga";
};

const VALID_LOADING_STATUSES = new Set([
  "LOADING_PENDING",
  "HEADING_TO_PICKUP",
  "WAITING_LOADING",
  "WAITING_DOCUMENTATION",
  "LOADING_COMPLETED",
]);

const VALID_UNLOADING_STATUSES = new Set([
  "UNLOADING_PENDING",
  "IN_TRANSIT",
  "WAITING_UNLOADING",
  "UNLOADING_COMPLETED",
]);

export const getStoppingPointStatusLabel = (stop) => {
  if (!stop) return "";

  const operation = stop.operation;
  const isCargaStop = hasCargaOperation(operation);
  const isDescargaStop = hasDescargaOperation(operation);

  if (isCargaStop) {
    const loadingStatusValue = String(stop.loadingStatus || "").toUpperCase();
    if (!VALID_LOADING_STATUSES.has(loadingStatusValue)) return "";
    return translatorForTheStatusOfStoppingPoints(loadingStatusValue);
  }

  if (isDescargaStop) {
    const unloadingStatusValue = String(
      stop.unloadingStatus || "",
    ).toUpperCase();
    if (!VALID_UNLOADING_STATUSES.has(unloadingStatusValue)) return "";
    return translatorForTheStatusOfStoppingPoints(unloadingStatusValue);
  }

  return "";
};

export const normalizeStopDocuments = (documentsValue) => {
  if (Array.isArray(documentsValue)) {
    return documentsValue;
  }

  if (documentsValue && typeof documentsValue === "object") {
    return Object.values(documentsValue);
  }

  return [];
};

const isValidDocumentItem = (documentItem) =>
  documentItem != null &&
  (typeof documentItem === "object" ||
    typeof documentItem === "string" ||
    typeof documentItem === "number");

const getInvoiceKeyFromUrl = (url, index) => {
  if (!url) return `NFE-${index + 1}`;
  const cleanUrl = String(url).split("?")[0];
  const decodedUrl = decodeURIComponent(cleanUrl);
  const parts = decodedUrl.split("/");
  const lastPart = parts[parts.length - 1];
  return lastPart || `NFE-${index + 1}`;
};

const normalizeInvoiceDocuments = (invoicesValue) => {
  return normalizeStopDocuments(invoicesValue)
    .map((invoiceItem, index) => {
      if (invoiceItem == null) return null;
      if (typeof invoiceItem === "object") {
        return {
          ...invoiceItem,
          type:
            invoiceItem.type ||
            invoiceItem.docType ||
            invoiceItem.documentType ||
            "INVOICE",
        };
      }

      const url = String(invoiceItem);
      return {
        type: "INVOICE",
        key: getInvoiceKeyFromUrl(url, index),
        url,
      };
    })
    .filter(Boolean);
};

export const getStoppingPointDocuments = (stop) => {
  const documents = normalizeStopDocuments(stop?.documents);
  const invoices = normalizeInvoiceDocuments(stop?.invoices);

  return [...documents, ...invoices].filter(isValidDocumentItem);
};

export const normalizeFreightAttachment = (
  attachment,
  fallbackStoppingPointId = "",
) => {
  if (!attachment || typeof attachment !== "object") return null;
  if (!attachment.url) return null;

  return {
    ...attachment,
    stoppingPointId:
      attachment.stoppingPointId || fallbackStoppingPointId || "",
  };
};

export const normalizeFreightCanhoto = (
  canhotoValue,
  fallbackStoppingPointId = "",
) => {
  if (!canhotoValue) return null;

  if (typeof canhotoValue === "string") {
    const normalizedUrl = canhotoValue.trim();
    if (!normalizedUrl) return null;

    return {
      stoppingPointId: fallbackStoppingPointId || "",
      url: normalizedUrl,
    };
  }

  if (typeof canhotoValue !== "object") return null;

  const normalizedUrl = String(
    canhotoValue.url || canhotoValue.canhoto || "",
  ).trim();
  if (!normalizedUrl) return null;

  return {
    ...canhotoValue,
    url: normalizedUrl,
    stoppingPointId:
      canhotoValue.stoppingPointId || fallbackStoppingPointId || "",
  };
};

export const normalizeCanhotosList = (
  canhotoValue,
  fallbackStoppingPointId = "",
) => {
  const isSingleCanhotoObject =
    canhotoValue &&
    typeof canhotoValue === "object" &&
    !Array.isArray(canhotoValue) &&
    ("url" in canhotoValue ||
      "canhoto" in canhotoValue ||
      "stoppingPointId" in canhotoValue);

  const rawCanhotos = Array.isArray(canhotoValue)
    ? canhotoValue
    : isSingleCanhotoObject
      ? [canhotoValue]
      : canhotoValue && typeof canhotoValue === "object"
        ? Object.values(canhotoValue)
        : typeof canhotoValue === "string"
          ? [canhotoValue]
          : [];

  return rawCanhotos
    .map((item) => normalizeFreightCanhoto(item, fallbackStoppingPointId))
    .filter(Boolean);
};

export const mergeCanhotoLists = (
  firstList = [],
  secondList = [],
  fallbackStoppingPointId = "",
) => {
  const mergedCanhotos = [];
  const seenCanhotos = new Set();

  [...firstList, ...secondList].forEach((canhotoItem) => {
    const normalizedCanhoto = normalizeFreightCanhoto(
      canhotoItem,
      fallbackStoppingPointId,
    );
    if (!normalizedCanhoto) return;

    const canhotoKey =
      normalizedCanhoto.id ||
      `${normalizedCanhoto.stoppingPointId}|${normalizedCanhoto.url}`;

    if (seenCanhotos.has(canhotoKey)) return;
    seenCanhotos.add(canhotoKey);
    mergedCanhotos.push(normalizedCanhoto);
  });

  return mergedCanhotos;
};

/**
 * Builds a list of freight canhotos from the given stops and existing canhotos.
 *
 * @param {Array<Object>} stops - The list of stops to build canhotos from.
 * @param {Array<Object>} existingCanhotos - The existing canhotos to add to the result.
 * @returns {Array<Object>} The resulting list of freight canhotos.
 */
export const buildFreightCanhotos = (stops = [], existingCanhotos = []) => {
  const canhotos = [];
  const seenCanhotos = new Set();

  const addCanhoto = (canhotoItem, fallbackStoppingPointId = "") => {

    const normalizedCanhoto = normalizeFreightCanhoto(
      canhotoItem,
      fallbackStoppingPointId,
    );
    if (!normalizedCanhoto) return;

    const canhotoKey =
      normalizedCanhoto.id ||
      `${normalizedCanhoto.stoppingPointId}|${normalizedCanhoto.url}`;

    if (seenCanhotos.has(canhotoKey)) return;
    seenCanhotos.add(canhotoKey);
    canhotos.push(normalizedCanhoto);
  };

  normalizeCanhotosList(existingCanhotos).forEach((item) => addCanhoto(item));

  stops.forEach((stop) => {
    if (!hasDescargaOperation(stop?.operation)) return;
    const fallbackStopId = stop?.id || "";
    normalizeCanhotosList(stop?.canhoto, fallbackStopId).forEach((item) =>
      addCanhoto(item, fallbackStopId),
    );
  });

  return canhotos;
};

export const buildFreightAttachments = (
  stops = [],
  existingAttachments = [],
) => {
  const attachments = [];
  const seenAttachments = new Set();

  const addAttachment = (attachment, fallbackStoppingPointId = "") => {
    const normalizedAttachment = normalizeFreightAttachment(
      attachment,
      fallbackStoppingPointId,
    );
    if (!normalizedAttachment) return;

    const attachmentKey =
      normalizedAttachment.id ||
      `${normalizedAttachment.url}|${normalizedAttachment.stoppingPointId}`;

    if (seenAttachments.has(attachmentKey)) return;
    seenAttachments.add(attachmentKey);
    attachments.push(normalizedAttachment);
  };

  existingAttachments.forEach((attachment) => addAttachment(attachment));

  stops.forEach((stop) => {
    const stopAttachments = Array.isArray(stop?.attachments)
      ? stop.attachments
      : [];
    const fallbackStopId = stop?.id || "";
    stopAttachments.forEach((attachment) =>
      addAttachment(attachment, fallbackStopId),
    );
  });

  return attachments;
};

export const normalizeAttachmentsList = (
  attachmentsValue,
  fallbackStoppingPointId = "",
) => {
  const rawAttachments = Array.isArray(attachmentsValue)
    ? attachmentsValue
    : attachmentsValue && typeof attachmentsValue === "object"
      ? Object.values(attachmentsValue)
      : [];

  return rawAttachments
    .map((attachment) =>
      normalizeFreightAttachment(attachment, fallbackStoppingPointId),
    )
    .filter(Boolean);
};

export const mergeAttachmentLists = (firstList = [], secondList = []) => {
  const mergedAttachments = [];
  const seenAttachments = new Set();

  [...firstList, ...secondList].forEach((attachment) => {
    const normalizedAttachment = normalizeFreightAttachment(attachment);
    if (!normalizedAttachment) return;

    const attachmentKey =
      normalizedAttachment.id ||
      `${normalizedAttachment.url}|${normalizedAttachment.stoppingPointId}`;

    if (seenAttachments.has(attachmentKey)) return;
    seenAttachments.add(attachmentKey);
    mergedAttachments.push(normalizedAttachment);
  });

  return mergedAttachments;
};

export const getAttachmentsByStoppingPointId = (
  attachments = [],
  stoppingPointId = "",
) => {
  const normalizedStoppingPointId = String(stoppingPointId || "");
  return normalizeAttachmentsList(attachments).filter(
    (attachment) =>
      String(attachment?.stoppingPointId || "") === normalizedStoppingPointId,
  );
};

export const getCanhotosByStoppingPointId = (
  canhotos = [],
  stoppingPointId = "",
) => {
  const normalizedStoppingPointId = String(stoppingPointId || "");
  return normalizeCanhotosList(canhotos).filter(
    (canhotoItem) =>
      String(canhotoItem?.stoppingPointId || "") === normalizedStoppingPointId,
  );
};

const DOCUMENT_LABEL_BY_TYPE = {
  INVOICE: "Nota Fiscal",
  NFE: "NF-e",
  CTE: "CT-e",
  MDFE: "MDF-e",
  DOCUMENTO: "Documento",
};

const normalizeDocumentType = (rawType) => {
  const normalized = String(rawType || "")
    .trim()
    .toUpperCase();
  if (!normalized) return "DOCUMENTO";
  if (normalized === "NF" || normalized === "NFE" || normalized === "NF-E") {
    return "NFE";
  }
  if (normalized === "CTE" || normalized === "CT-E") {
    return "CTE";
  }
  if (normalized === "MDFE" || normalized === "MDF-E") {
    return "MDFE";
  }
  if (normalized === "INVOICE") {
    return "INVOICE";
  }
  return normalized;
};

export const formatStopDocument = (documentItem, index = 0) => {
  let typeValue = "DOCUMENTO";
  let keyValue = "";
  let urlValue = "";

  if (typeof documentItem === "string" || typeof documentItem === "number") {
    keyValue = String(documentItem);
  } else if (documentItem && typeof documentItem === "object") {
    typeValue =
      documentItem.type ||
      documentItem.docType ||
      documentItem.documentType ||
      "DOCUMENTO";
    keyValue =
      documentItem.key ??
      documentItem.chave ??
      documentItem.number ??
      documentItem.value ??
      documentItem.documentKey ??
      "";
    urlValue = documentItem.url ? String(documentItem.url) : "";
  }

  const normalizedType = normalizeDocumentType(typeValue);
  const label = DOCUMENT_LABEL_BY_TYPE[normalizedType] || "Documento";

  let value = String(keyValue || "").trim();

  if (normalizedType === "INVOICE") {
    value = "Nota Fiscal de Transporte";
  } else if (!value && urlValue) {
    value = getInvoiceKeyFromUrl(urlValue, index);
  }
  if (!value) {
    value = "Abrir documento";
  }

  return { label, value, url: urlValue };
};

export const isStopLockedForEdition = (stop) => {
  if (!stop) return false;

  const isCargaStop = hasCargaOperation(stop.operation);
  const loadingStatusValue = String(stop.loadingStatus || "").toUpperCase();

  return isCargaStop && loadingStatusValue === "LOADING_COMPLETED";
};

export const normalizeStopCanhotoForPayload = (canhoto) => {
  if (Array.isArray(canhoto)) {
    return canhoto.length > 0 ? canhoto : "";
  }
  if (canhoto == null) return "";
  if (typeof canhoto === "string") return canhoto.trim() ? canhoto : "";
  return canhoto;
};

export const resolveStopTypeOperation = (operation = [], fallback = "") => {
  const ops = Array.isArray(operation) ? operation : [];
  const hasCarga = ops.includes("carga");
  const hasDescarga = ops.includes("descarga");
  if (hasCarga) return "LOADING";
  if (hasDescarga) return "UNLOADING";
  const normalizedFallback = String(fallback || "").trim().toUpperCase();
  if (normalizedFallback === "LOADING" || normalizedFallback === "UNLOADING") {
    return normalizedFallback;
  }
  return "";
};

export const applyDefaultStopStatuses = (stops = []) => {
  return stops.map((stop) => {
    const operation = Array.isArray(stop.operation) ? stop.operation : [];

    return {
      ...stop,
      attachments: Array.isArray(stop.attachments) ? stop.attachments : [],
      canhoto: normalizeStopCanhotoForPayload(stop.canhoto),
      loadingStatus: operation.includes("carga")
        ? stop.loadingStatus || "LOADING_PENDING"
        : stop.loadingStatus || "",
      unloadingStatus: operation.includes("descarga")
        ? stop.unloadingStatus || "UNLOADING_PENDING"
        : stop.unloadingStatus || "",
      typeOperation: resolveStopTypeOperation(operation, stop.typeOperation),
    };
  });
};

export const toDisplayNumeric = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  return value;
};

export const isStopCargoFieldEmpty = (value) =>
  value === null || value === undefined || value === "";

export const parseStopNumericValue = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  const s = String(value).replace(/\./g, "").replace(",", ".");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
};

export const hasValidCargoFieldInStops = (stops = [], field) => {
  if (!Array.isArray(stops) || !field) return false;
  return stops.some((stop) => {
    if (!hasCargaOperation(stop?.operation)) return false;
    const n = parseStopNumericValue(stop?.[field]);
    return Number.isFinite(n) && n > 0;
  });
};

export const sumCargoStopsNumber = (stops = [], field) => {
  if (!Array.isArray(stops)) return 0;
  return stops.reduce((acc, stop) => {
    if (!hasCargaOperation(stop?.operation)) return acc;
    const n = parseStopNumericValue(stop?.[field]);
    if (!Number.isFinite(n)) return acc;
    return acc + n;
  }, 0);
};

export const resolveGlobalCargoValues = (stops = [], freightSummary = {}) => {
  const legacyWeight = toDisplayNumeric(freightSummary?.weightCargo, 0);
  const legacyValueNF = toDisplayNumeric(freightSummary?.valueNF, "");

  const hasWeightInStops = hasValidCargoFieldInStops(stops, "cargo_weight_kg");
  const hasValueNFInStops = hasValidCargoFieldInStops(stops, "cargo_value_nf");

  const weightCargo = hasWeightInStops
    ? sumCargoStopsNumber(stops, "cargo_weight_kg")
    : legacyWeight;
  const valueNF = hasValueNFInStops
    ? sumCargoStopsNumber(stops, "cargo_value_nf")
    : legacyValueNF;

  return { weightCargo, valueNF };
};

export const hydrateStopsCargoFromFreightSummary = (
  stops = [],
  freightSummary = {},
) => {
  if (!Array.isArray(stops) || stops.length === 0) return stops;

  const firstCargoIndex = stops.findIndex((stop) =>
    hasCargaOperation(stop?.operation),
  );
  if (firstCargoIndex === -1) return stops;

  const hasWeightInStops = hasValidCargoFieldInStops(stops, "cargo_weight_kg");
  const hasValueNFInStops = hasValidCargoFieldInStops(stops, "cargo_value_nf");

  if (hasWeightInStops && hasValueNFInStops) return stops;

  const legacyWeight = toDisplayNumeric(freightSummary?.weightCargo, "");
  const legacyValueNF = toDisplayNumeric(freightSummary?.valueNF, "");

  const hydratedStops = [...stops];
  const targetStop = { ...hydratedStops[firstCargoIndex] };

  if (!hasWeightInStops && !isStopCargoFieldEmpty(legacyWeight)) {
    targetStop.cargo_weight_kg = legacyWeight;
  }
  if (!hasValueNFInStops && !isStopCargoFieldEmpty(legacyValueNF)) {
    targetStop.cargo_value_nf = legacyValueNF;
  }

  if (
    targetStop.cargo_weight_kg ===
    hydratedStops[firstCargoIndex].cargo_weight_kg &&
    targetStop.cargo_value_nf === hydratedStops[firstCargoIndex].cargo_value_nf
  ) {
    return stops;
  }

  hydratedStops[firstCargoIndex] = targetStop;
  return hydratedStops;
};

export const formatName = (name) => {
  if (!name) return "";
  return name
    .toLowerCase()
    .split(" ")
    .map((word) => {
      // if (['da', 'de', 'do', 'dos', 'das'].includes(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

export const geraNumeroColeta = () => {
  const digitosUnicos = new Set();

  while (digitosUnicos.size < 7) {
    const digito = Math.floor(Math.random() * 10);
    digitosUnicos.add(digito);
  }

  const numero = Array.from(digitosUnicos).join("");
  return numero;
};

export function calcularDistancia(
  latitudeOrigem,
  longitudeOrigem,
  latitudeDestino,
  longitudeDestino,
) {


  const origem = { latitude: latitudeOrigem, longitude: longitudeOrigem };
  const destino = { latitude: latitudeDestino, longitude: longitudeDestino };

  console.log("Origem:", origem);
  console.log("Destino:", destino);

  const distanciaMetros = getDistance(origem, destino);
  const distanciaKm = (distanciaMetros / 1000).toFixed(2);

  return distanciaKm;
}

export function withoutMaskPhone(input) {
  return input
    ? input.replace("(", "").replace(")", "").replace(" ", "").replace("-", "")
    : "";
}

export const formatCPF = (cpf) => {
  if (!cpf) return "";
  return cpf.replace(/(\\d{3})(\\d{3})(\\d{3})(\\d{2})/, "$1.$2.$3-$4");
};

const FREIGHT_SCREEN_SIMULATION_DEFAULTS = {
  axis: "6",
  cargoType: "geral",
  fuelConsumption: {
    fuelPrice: "6,19",
    kmFuel: "2,8",
  },
  rates: {
    taxaAdm: 0,
    taxaGiro: 0,
    impostos: 0,
  },
  route: {
    calculateReturn: false,
    optimizedRoute: true,
    typeRoute: "efficient",
  },
  vehicleRate: 3.8,
  vehicleType: "truck",
  topSpeed: 80,
};

const VEHICLE_AXLES_BY_NAME = {
  FIORINO: 2,
  "3/4": 2,
  TOCO: 2,
  VUC: 2,
  TRUCK: 3,
  "BI-TRUCK": 4,
  "CARRETA S": 5,
  "CARRETA LS": 6,
  VANDERLEIA: 6,
  "BI-TREM": 9,
  BITREM: 9,
  RODOTREM: 9,
};

const validKeys = [
  'granel_solido',
  'granel_liquido',
  'frigorificada',
  'conteineirizada',
  'geral',
  'neogranel',
  'perigosa_granel_solido',
  'perigosa_granel_liquido',
  'perigosa_frigorificada',
  'perigosa_conteineirizada',
  'perigosa_geral',
  'granel_pressurizada',
];

function getFreightScreenAxis(vehicleData) {
  const directAxles =
    typeof vehicleData === "string" || typeof vehicleData === "number"
      ? vehicleData
      : "";
  const informedAxles = String(
    directAxles || vehicleData?.axles || vehicleData?.axis || "",
  ).trim();

  if (informedAxles) {
    return informedAxles === "11" ? "all" : informedAxles;
  }

  const selectedAxles = Array.isArray(vehicleData?.typeVehicle?.dados)
    ? vehicleData.typeVehicle.dados
      .filter((vehicle) => vehicle?.selected)
      .map((vehicle) => VEHICLE_AXLES_BY_NAME[vehicle.name])
      .filter((axles) => Number.isFinite(axles))
    : [];

  if (!selectedAxles.length) return FREIGHT_SCREEN_SIMULATION_DEFAULTS.axis;

  const highestAxleQuantity = Math.max(...selectedAxles);
  return highestAxleQuantity === 11 ? "all" : String(highestAxleQuantity);
}

export const calculateGrossFreightProposedByShipperAction = async (data) => {
  const grossFreightResult = await qualPApi.calculateGrossFreightProposedByShipper(data);
  return grossFreightResult.data.data;
};

export async function calculateFromFreightScreen(data) {


  const user = data.users;
  const uidShipper = data.shipper.uid || "";
  const simulationPayload = buildFreightScreenSimulationPayload(data);

  const validFieldsSimulation = {
    stopping_points: data.stoppingPoints,
    cargoType: normalizeCargoTypeKey(data.stoppingPoints),
    axles: Number(data.freight.axles),
    valueNF: Number(normalizePtNumber(data.freight.valueNF)),
    weightCargo: Number(normalizePtNumber(data.freight.weightCargo)),
  };

  const validSimulation = getValidFieldsSimulation(validFieldsSimulation);

  if (!validSimulation.success) {
    return { success: false, errors: validSimulation.errors };
  }

  const simulationResponse = await qualPApi.postQualP(
    simulationPayload,
    uidShipper,
  );

  const createFreightPayload = buildFreightFromSimulationPayload({
    data,
    simulationData: simulationResponse.data.data,
    user,
  });

  const snapshot =
    await qualPApi.createFreightFromSimulation({ ...createFreightPayload, stoppingPoints: data.stoppingPoints, freight: data.freight });

  return snapshot.data.data;
}

const getValidFieldsSimulation = (fieldsSimulation) => {
  const validFieldsSimulationSchema =
    simulationSchema.safeParse(fieldsSimulation);

  if (!validFieldsSimulationSchema.success) {
    const errors = validFieldsSimulationSchema.error.issues.map(
      (error) => error.message,
    );

    return { success: false, errors };
  }

  return { success: true, errors: [] };
};

const buildFreightScreenSimulationPayload = (data) => {
  const routeFrete = buildRouteFrete(data.stoppingPoints);
  const validDestinations = getValidDestinations(data.stoppingPoints);
  const valueNF = normalizePtNumber(data.freight.valueNF);
  const weightCargo = normalizePtNumber(data.freight.weightCargo);
  const axles = getFreightScreenAxis(data.freight.axles);
  const cargoType = normalizeCargoTypeKey(data.stoppingPoints);

  return {
    locations: routeFrete,
    vehicle: {
      type: FREIGHT_SCREEN_SIMULATION_DEFAULTS.vehicleType,
      axles: axles,
      top_speed: FREIGHT_SCREEN_SIMULATION_DEFAULTS.topSpeed,
    },
    route: {
      optimized_route: FREIGHT_SCREEN_SIMULATION_DEFAULTS.route.optimizedRoute,
      calculate_return:
        FREIGHT_SCREEN_SIMULATION_DEFAULTS.route.calculateReturn,
      type_route: FREIGHT_SCREEN_SIMULATION_DEFAULTS.route.typeRoute,
    },
    fuel_consumption: {
      fuel_price: FREIGHT_SCREEN_SIMULATION_DEFAULTS.fuelConsumption.fuelPrice,
      km_fuel: FREIGHT_SCREEN_SIMULATION_DEFAULTS.fuelConsumption.kmFuel,
    },
    simulation: {
      destinations: validDestinations,
      axles: axles,
      value: toPtDecimalString(valueNF, "0,00"),
      weight: toPtDecimalString(weightCargo, "0,00"),
      vehicle: FREIGHT_SCREEN_SIMULATION_DEFAULTS.vehicleType,
      vehicleRate: FREIGHT_SCREEN_SIMULATION_DEFAULTS.vehicleRate,
      optimizeRoute: FREIGHT_SCREEN_SIMULATION_DEFAULTS.route.optimizedRoute,
      cargoType,
      rates: { ...FREIGHT_SCREEN_SIMULATION_DEFAULTS.rates },
    },
  };
};

const normalizeCargoTypeKey = (stoppingPoints = []) => {
  const cargoTypeIndex = parseInt(stoppingPoints[0]?.type);
  return validKeys[cargoTypeIndex] || FREIGHT_SCREEN_SIMULATION_DEFAULTS.cargoType;
};

const buildFreightFromSimulationPayload = ({ data, simulationData, user }) => {
  const valueNF = normalizePtNumber(data.freight.valueNF);
  const weightCargo = normalizePtNumber(data.freight.weightCargo);
  const axles = getFreightScreenAxis(data.freight.axles);

  return {
    uidShipper: user.uidShipper,
    ...simulationData,
    // Preserva o serial do frete em edição (createFreightFromSimulation usa se vier no payload)
    numberSerial: data.numberSerial,
    createUser: user.email,
    user: { uid: user.uid, name: user.name },
    axles: axles,
    typeVehicle: FREIGHT_SCREEN_SIMULATION_DEFAULTS.vehicleType,
    value: toPtDecimalString(valueNF, "0,00"),
    weightCargo: toPtDecimalString(weightCargo, "0,00"),
    weightUnit: data.freight.weightUnit || "kg",
    cargoType: FREIGHT_SCREEN_SIMULATION_DEFAULTS.cargoType,
  };
};

const normalizePtNumber = (value) =>
  String(value || "0")
    .replace(/\./g, "")
    .replace(",", ".");

const buildRouteFrete = (data) => {
  const route = [];

  data.sort((a, b) => a.ordem - b.ordem);

  data.forEach((stop) => {
    const { logradouro, number, city, uf } = stop;

    if(logradouro == '' ){
      route.push(`${city}, ${uf}, Brasil`);
    } else if ( number  <= 0) {
      route.push(` ${city}, ${uf}, Brasil`);
    }else{
      route.push(`${logradouro}, ${number} - ${city}, ${uf}, Brasil`);
      
    }


    
  });

  return route;
};

const getValidDestinations = (data) => {
  const destinations = [];

  data.forEach((stop) => {
    const { logradouro, number, city, uf } = stop;
    if (stop.operation[0] === "descarga") {
      destinations.push(`${logradouro}, ${number} - ${city}, ${uf}`);
    }
  });
  return destinations;
};

function toPtDecimalString(value, fallback = "0,00") {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return fallback;
  return numericValue.toFixed(2).replace(".", ",");
}
